import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, ChevronLeft, Hand, Check, X, RotateCcw, Paperclip } from 'lucide-react';
import ModulLayout from '../../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../../layout/ListToolbar';
import { SECTION_COLOR, SECTION_BG, hlavicky, formatCasListu } from '../komunikace';
import { DRUHY_UKOLU, STAVY_UKOLU, formatTermin, poTerminu } from './ukolyUi';
import NovyUkol from './NovyUkol';
import ZpetTlacitko from '../../ui/ZpetTlacitko';
import PrilohyMrizka from '../prilohy/PrilohyMrizka';
import PrilohyVyber from '../prilohy/PrilohyVyber';

/** Starší fotky hlášení (před přílohami) ve tvaru přílohy, ať je kreslí tatáž mřížka. */
const zFotky = (url, i) => ({ id: `f${i}`, nazev: `Fotka ${i + 1}`, je_obrazek: true, url, nahled_url: url, stahnout_url: url });

/**
 * Komunikace → Úkoly (etapa 6): úkoly konkrétním lidem a hlášení z provozu
 * (závada, vozidlo, surovina, reklamace). Norma §1: seznam · detail · panel.
 * Hlášení bez řešitele čeká na skupinu („Beru si to"), úkol má jméno.
 */
const POHLEDY = [
    { id: 'moje', label: 'Pro mě', upozorneni: true, patri: u => u.moje && ['otevreno', 'resi_se'].includes(u.stav) },
    { id: 'zadal', label: 'Zadal(a) jsem', patri: u => u.zadal_jsem },
    { id: 'otevrene', label: 'Otevřené', patri: u => ['otevreno', 'resi_se'].includes(u.stav) },
    { id: 'hotove', label: 'Vyřešené', patri: u => ['hotovo', 'zamitnuto'].includes(u.stav) },
];

export default function Ukoly({ isDark, isMobile, cardStyle, inputStyle, mutedColor, initialId = null, rezervaDole = null }) {
    const [data, setData] = useState(null);
    const [pohled, setPohled] = useState(null);
    const [hledani, setHledani] = useState('');
    const [vybranyId, setVybranyId] = useState(initialId ? Number(initialId) : null);
    const [nove, setNove] = useState(false);
    const [vysledek, setVysledek] = useState('');
    const [bezi, setBezi] = useState(null);
    // Fotka „hotovo" / k zamítnutí — přikládá řešitel při vyřešení.
    const [prilohyVyreseni, setPrilohyVyreseni] = useState([]);
    const [nahravam, setNahravam] = useState(false);

    useEffect(() => { if (initialId) setVybranyId(Number(initialId)); }, [initialId]);

    const nacti = useCallback(() => {
        fetch('/api/ukoly').then(r => (r.ok ? r.json() : null)).then(d => d && setData(d)).catch(() => {});
    }, []);
    useEffect(() => { nacti(); }, [nacti]);
    useEffect(() => { setVysledek(''); setPrilohyVyreseni([]); }, [vybranyId]);

    const ukoly = data?.ukoly || [];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, ukoly.filter(p.patri).length])), [ukoly]);
    const aktivniId = pohled || (pocty.moje > 0 ? 'moje' : 'otevrene');
    const aktivniPohled = POHLEDY.find(p => p.id === aktivniId) || POHLEDY[2];
    const hledat = hledani.trim().toLowerCase();
    const seznam = ukoly.filter(u => aktivniPohled.patri(u) && (!hledat || `${u.nazev} ${u.popis || ''} ${u.zalozil} ${u.resitel || ''}`.toLowerCase().includes(hledat)));
    const vybrany = ukoly.find(u => u.id === vybranyId) || null;

    const akce = async (co) => {
        setBezi(co);
        try {
            const res = await fetch(`/api/ukoly/${vybranyId}/${co}`, { method: 'POST', headers: hlavicky(), body: JSON.stringify({ vysledek: vysledek.trim() || null, prilohy: ['hotovo', 'zamitnout'].includes(co) ? prilohyVyreseni.map(p => p.id) : [] }) });
            if (res.ok) { setPrilohyVyreseni([]); nacti(); window.dispatchEvent(new Event('ukoly-zmena')); }
        } finally { setBezi(null); }
    };

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;
    const hrana = isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)';

    const stavChip = (u) => (
        <span className="shrink-0 text-[11px] font-semibold rounded-full" style={{
            padding: '2px 8px',
            background: u.stav === 'otevreno' ? SECTION_BG : 'transparent',
            color: u.stav === 'zamitnuto' ? '#F87171' : u.stav === 'otevreno' ? SECTION_COLOR : muted,
            border: `1px solid ${u.stav === 'otevreno' ? 'transparent' : hrana}`,
        }}>{STAVY_UKOLU[u.stav]}</span>
    );

    const radek = (u, siroky) => {
        const { Icon } = DRUHY_UKOLU[u.druh] || DRUHY_UKOLU.ukol;
        const aktivni = u.id === vybranyId;
        const komu = u.resitel || u.skupina;
        return (
            <button key={u.id} type="button" onClick={() => setVybranyId(u.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 14px' : '10px 14px', minHeight: 60, background: aktivni ? SECTION_BG : 'transparent', boxShadow: aktivni ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: u.nalehave ? '#F87171' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={17} />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5, textDecoration: u.stav === 'hotovo' ? 'line-through' : 'none' }}>
                        {u.nazev}
                        {(u.prilohy?.length > 0 || u.fotky?.length > 0) && <Paperclip size={12} aria-label="Má přílohy" style={{ color: muted, marginLeft: 6, verticalAlign: -1 }} />}
                    </p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 13 : 12, color: muted }}>
                        {[u.zadal_jsem ? (komu ? `→ ${komu}` : null) : u.zalozil, u.vozidlo].filter(Boolean).join(' · ')}
                        {u.termin && <span style={{ color: poTerminu(u) ? '#F87171' : undefined, fontWeight: poTerminu(u) ? 600 : 400 }}> · do {formatTermin(u.termin)}</span>}
                        {u.nalehave && <span style={{ color: '#F87171', fontWeight: 600 }}> · naléhavé</span>}
                    </p>
                </div>
                {siroky && <span className="shrink-0 text-xs" style={{ width: 110, color: muted }}>{DRUHY_UKOLU[u.druh]?.nazev}</span>}
                {siroky && <span className="shrink-0 text-xs" style={{ width: 80, color: muted }}>{formatCasListu(u.vytvoreno_at)}</span>}
                {stavChip(u)}
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat úkol, hlášení…', placeholderKratky: 'Hledat' }}
                akce={[{ id: 'nove', label: 'Nahlásit', Icon: Plus, primarni: true, onClick: () => setNove(true) }]}
                pohledy={!siroky && !isMobile ? POHLEDY.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id], upozorneni: p.upozorneni, barva: p.upozorneni ? SECTION_COLOR : undefined })) : null}
                pohledAktivni={aktivniPohled.id}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={aktivniPohled.id === p.id}
                        upozorneni={p.upozorneni && pocty[p.id] > 0} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {data === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data && seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledat ? 'Nic se neshoduje' : aktivniPohled.id === 'moje' ? 'Nic pro tebe. Závadu, chybějící surovinu nebo problém s autem nahlásíš přes „Nahlásit".' : 'Nic tu není.'}
                    </p>
                )}
                {seznam.map(u => radek(u, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detailPanel = () => {
        const u = vybrany;
        const tlac = (co, text, Icon, kladne, cervene) => (
            <button key={co} type="button" onClick={() => akce(co)} disabled={!!bezi || nahravam} className="flex items-center justify-center gap-1.5 rounded-lg font-semibold"
                style={{ height: isMobile ? 46 : 38, padding: '0 16px', fontSize: 14, cursor: 'pointer', flex: isMobile ? 1 : undefined, background: kladne ? SECTION_COLOR : 'transparent', color: kladne ? '#fff' : cervene ? '#F87171' : 'var(--text-primary)', border: kladne ? 'none' : `1px solid ${hrana}` }}>
                {bezi === co ? '…' : <><Icon size={15} /> {text}</>}
            </button>
        );
        const pole = (l, h, cervene = false) => (h ? (
            <div className="flex justify-between gap-4 text-[13.5px]" style={{ minHeight: 36, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{l}</span><span className="text-right" style={{ color: cervene ? '#F87171' : 'var(--text-primary)', fontWeight: 600 }}>{h}</span>
            </div>
        ) : null);
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '10px 14px 12px' : '16px 18px 12px', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    {isMobile && (
                        <ZpetTlacitko onClick={() => setVybranyId(null)} popis="Zpět na úkoly" sklo isDark={isDark} style={{ marginBottom: 10 }} />
                    )}
                    {u ? (
                        <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <h2 className="font-bold" style={{ fontSize: isMobile ? 18 : 17, color: 'var(--text-primary)', margin: 0 }}>{u.nazev}</h2>
                                <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>{u.druh_nazev} · {u.zalozil} · {formatCasListu(u.vytvoreno_at)}</p>
                            </div>
                            {stavChip(u)}
                        </div>
                    ) : <p className="text-sm" style={{ color: muted }}>Už v seznamu není.</p>}
                </div>
                {u && (
                    <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '12px 16px' : '14px 18px' }}>
                        {pole('Řeší', u.resitel || (u.skupina ? `${u.skupina} (kdokoli)` : null))}
                        {pole('Termín', u.termin ? `${formatTermin(u.termin)}${poTerminu(u) ? ' · po termínu' : ''}` : null, poTerminu(u))}
                        {pole('Vozidlo', u.vozidlo)}
                        {pole('Priorita', u.nalehave ? 'Naléhavé' : null)}
                        {u.popis && <p className="text-sm whitespace-pre-line" style={{ color: 'var(--text-primary)', margin: '14px 0 0', lineHeight: 1.55 }}>{u.popis}</p>}
                        {(() => {
                            const zadani = [...(u.fotky || []).map(zFotky), ...(u.prilohy || []).filter(p => p.ucel !== 'vyreseni')];
                            return zadani.length > 0 && (
                                <div style={{ marginTop: 14 }}><PrilohyMrizka prilohy={zadani} isDark={isDark} velikostNahledu={isMobile ? 96 : 110} /></div>
                            );
                        })()}
                        {(u.vysledek || (u.prilohy || []).some(p => p.ucel === 'vyreseni')) && u.vyreseno_at && (
                            <div style={{ marginTop: 16, padding: '10px 12px', borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                                <p className="text-xs" style={{ color: muted, margin: 0 }}>{STAVY_UKOLU[u.stav]} · {u.vyresil} · {formatCasListu(u.vyreseno_at)}</p>
                                {u.vysledek && <p className="text-sm" style={{ color: 'var(--text-primary)', margin: '4px 0 0' }}>{u.vysledek}</p>}
                                {(u.prilohy || []).some(p => p.ucel === 'vyreseni') && (
                                    <div style={{ marginTop: 8 }}><PrilohyMrizka prilohy={u.prilohy.filter(p => p.ucel === 'vyreseni')} isDark={isDark} velikostNahledu={isMobile ? 80 : 88} /></div>
                                )}
                            </div>
                        )}
                        {u.akce.length > 0 && (
                            <div style={{ marginTop: 20 }}>
                                {(u.akce.includes('hotovo') || u.akce.includes('zamitnout')) && (
                                    <>
                                        <textarea value={vysledek} onChange={e => setVysledek(e.target.value)} rows={2} placeholder="Co se udělalo (nepovinné) — klidně přilož fotku"
                                            style={{ width: '100%', borderRadius: 8, padding: '10px 12px', outline: 'none', fontSize: 14, marginBottom: 8, background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${hrana}`, color: 'var(--text-primary)' }} />
                                        <div style={{ marginBottom: 12 }}>
                                            <PrilohyVyber typ="ukol" prilohy={prilohyVyreseni} onChange={setPrilohyVyreseni} onNahravani={setNahravam} isDark={isDark} popisek={false} kompaktni />
                                        </div>
                                    </>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {u.akce.includes('prevzit') && tlac('prevzit', 'Beru si to', Hand, false)}
                                    {u.akce.includes('hotovo') && tlac('hotovo', 'Hotovo', Check, true)}
                                    {u.akce.includes('zamitnout') && tlac('zamitnout', 'Zamítnout', X, false, true)}
                                    {u.akce.includes('znovu') && tlac('znovu', 'Otevřít znovu', RotateCcw, false)}
                                </div>
                            </div>
                        )}
                        {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                    </div>
                )}
            </div>
        );
    };

    const panel = () => {
        const otevrene = ukoly.filter(u => u.moje && ['otevreno', 'resi_se'].includes(u.stav));
        const radekP = (l, h, cervene) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: muted }}>{l}</span><b style={{ color: cervene && h > 0 ? '#F87171' : 'var(--text-primary)' }}>{h}</b>
            </div>
        );
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 8px' }}>Pro mě</p>
                {radekP('Otevřené', otevrene.length)}
                {radekP('Po termínu', otevrene.filter(poTerminu).length, true)}
                {radekP('Naléhavé', otevrene.filter(u => u.nalehave).length, true)}
                <p className="text-xs" style={{ color: muted, marginTop: 12 }}>
                    Hlášení jde podle druhu: závada → Údržba, auto → Flotila, surovina → Suroviny a vedoucí, reklamace → Objednávky. Úkol ze zprávy zadáš v chatu přes menu zprávy.
                </p>
            </div>
        );
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />
            {nove && <NovyUkol rezim="hlaseni" isDark={isDark} onClose={() => setNove(false)} onHotovo={(u) => { setNove(false); nacti(); setVybranyId(u.id); setPohled('zadal'); }} />}
        </div>
    );
}
